import clsx from 'clsx';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { AppButton } from 'src/components/uikit/buttons/button/button.component';
import { AppCheckboxGroupField } from 'src/components/uikit/fields/checkboxGroup/AppCheckboxGroupField.component';
import { AppRadioGroupField } from 'src/components/uikit/fields/radio/appRadioGroupField.component';
import { AppSliderField } from 'src/components/uikit/fields/slider/appSliderField.component';
import { AppTextField } from 'src/components/uikit/fields';
import { ContentLayout, ISurveyContentBlock, ISurveyQuestion, QuestionType } from 'src/services/api/api-client';
import { SliderColorSchemeEnum } from 'uikit/inputs/slider/appSliderInput';
import { SurveyAttentionText } from 'src/components/expandedRow/surveys/surveyFilling/surveyFilling.component';
import { AppInputLabel } from 'uikit/wrappers';
import Style from './questionPreview.module.css';

type PreviewMode = 'desktop' | 'mobile';

const isContentQuestion = (questionType: QuestionType) =>
  questionType === QuestionType.Content || questionType === QuestionType.Gallery;

const shouldPinControlsToBottom = (questionType: QuestionType) =>
  ![QuestionType.FreeInput, QuestionType.DateInput, QuestionType.DateRangeInput].includes(questionType);

const mobileSurveyTitle = 'Your demo survey';
const attentionPrefix = 'Pay attention!';

const MobileAttentionText = ({ text, centered }: { text?: string | null; centered?: boolean }) =>
  text?.trim() ? (
    <div
      className={clsx(centered ? Style.mobileSupportTextTop : Style.mobileSupportText, Style.mobileAttentionWrapper)}
    >
      <span className={Style.mobileAttentionPrefix}>{attentionPrefix}</span>
      <span>{` ${text}`}</span>
    </div>
  ) : null;

const useSelectedValues = (question: ISurveyQuestion | null) => {
  return useMemo(() => {
    if (!question?.options?.length) {
      return { answerValue: undefined, scoreValue: undefined, checkedValues: [] as string[] };
    }

    const answered = question.options.filter((option) => option.optionValue === option.answerValue);
    return {
      answerValue:
        answered[0]?.optionValue ??
        question.options.find((option) => option.optionValue === option.defaultValue)?.optionValue,
      scoreValue: question.options.find((option) => option.optionValue === option.answerValue)?.scoreValue,
      checkedValues: answered.map((option) => option.optionValue),
    };
  }, [question]);
};

const MobileChrome = ({ children }: { children: React.ReactNode }) => (
  <div className={Style.mobilePhone}>
    <div className={Style.mobileStatusBar}>
      <span>{'•••'}</span>
      <span>{'9:42 Uhr'}</span>
      <span>{'4G'}</span>
    </div>
    <div className={Style.mobileBody}>{children}</div>
  </div>
);

const MobileNav = ({
  onPrev,
  onNext,
  counterText,
}: {
  onPrev?: () => void;
  onNext?: () => void;
  counterText: string;
}) => (
  <div className={Style.mobileNav}>
    <button className={Style.navButton} onClick={onPrev}>
      {'‹'}
    </button>
    <div className={Style.navCounter}>{counterText}</div>
    <button className={Style.navButton} onClick={onNext}>
      {'›'}
    </button>
  </div>
);

const MobileUnsupported = ({ questionType }: { questionType: QuestionType }) => (
  <div className={Style.unsupported}>{`${QuestionType[questionType]} type is not supported`}</div>
);

const ContentImage = ({ resourceKey }: { resourceKey?: string | null }) =>
  resourceKey ? <div className={Style.contentImage}>{resourceKey}</div> : null;

const ContentBlockPreview = ({ block }: { block: ISurveyContentBlock }) => {
  const layout = block.layout ?? ContentLayout.Default;

  if (layout === ContentLayout.IllustrationCentered) {
    return (
      <div className={Style.contentBlockIllustration}>
        <div className={Style.contentTitle}>{block.titleText || 'Untitled block'}</div>
        <ContentImage resourceKey={block.image?.resourceKey} />
        <div className={Style.contentDescription}>{block.descriptionText || 'Description'}</div>
      </div>
    );
  }

  if (layout === ContentLayout.StepCard) {
    return (
      <div className={Style.contentBlockStep}>
        <ContentImage resourceKey={block.image?.resourceKey} />
        <div className={Style.contentStepTitle}>{block.titleText || 'Step title'}</div>
        <div className={Style.contentDescription}>{block.descriptionText || 'Step explanation'}</div>
      </div>
    );
  }

  return (
    <div className={Style.contentBlockDefault}>
      <div className={Style.contentTitle}>{block.titleText || 'Untitled block'}</div>
      <div className={Style.contentDescription}>{block.descriptionText || 'Description'}</div>
      <ContentImage resourceKey={block.image?.resourceKey} />
    </div>
  );
};

const ContentPreview = ({ question, prefix }: { question: ISurveyQuestion; prefix?: string }) => {
  const blocks = question.contentBlock ?? [];
  const isGallery = question.questionType === QuestionType.Gallery;

  return (
    <div className={Style.contentPreview}>
      {prefix !== undefined ? (
        <AppInputLabel isBold={true} text={question.questionText || 'Untitled screen'} prefix={prefix} />
      ) : (
        <div className={Style.contentQuestionTitle}>{question.questionText || 'Untitled screen'}</div>
      )}
      {question.attentionText && <div className={Style.contentAttention}>{question.attentionText}</div>}
      <div className={Style.contentBlocks}>
        {blocks.map((block, index) => (
          <div key={index} className={Style.contentSlide}>
            {isGallery && <div className={Style.contentSlideLabel}>{`Slide ${index + 1}`}</div>}
            <ContentBlockPreview block={block} />
          </div>
        ))}
      </div>
      <div className={Style.contentAction}>{'Continue'}</div>
    </div>
  );
};

const renderDesktopPreview = (
  question: ISurveyQuestion,
  index: string,
  answerValue?: string,
  scoreValue?: number,
  checkedValues?: string[],
  onDefaultValueChange?: (optionValue: string | null) => void,
) => {
  if (isContentQuestion(question.questionType)) {
    return <ContentPreview question={question} prefix={index} />;
  }

  const labelProps = {
    isBold: true,
    text: question.questionText,
    prefix: index,
    underLabelComponent: (
      <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
    ),
  };

  const skipProps = {
    inputCanBeSkipped: !!question.canBeSkipped,
    showReasons: false,
    skipReason: undefined,
    onSkipReasonChange: () => undefined,
  };

  switch (question.questionType) {
    case QuestionType.SingleChose:
    case QuestionType.YesNo:
    case QuestionType.Rate:
    case QuestionType.ConditionalQuestion:
      return (
        <AppRadioGroupField
          labelProps={labelProps}
          skipProps={skipProps}
          isVertical={!question.isYesNoQuestion}
          options={question.options ?? []}
          labelField={'optionText'}
          valueField={'optionValue'}
          value={answerValue}
          onChange={(option) => onDefaultValueChange?.(option.optionValue)}
        />
      );
    case QuestionType.MultipleChose:
      return (
        <AppCheckboxGroupField
          labelProps={labelProps}
          skipProps={skipProps}
          options={question.options ?? []}
          labelField={'optionText'}
          valueField={'optionValue'}
          value={checkedValues}
          onChange={(value) => onDefaultValueChange?.(value?.[0] ?? null)}
        />
      );
    case QuestionType.Scale:
      return (
        <AppSliderField
          labelProps={labelProps}
          skipProps={skipProps}
          startWith={_.first(question.options)?.scoreValue ?? 0}
          endWith={_.last(question.options)?.scoreValue ?? 9}
          value={scoreValue ?? (Number(question.options?.[0]?.defaultValue) || undefined)}
          caption={{
            startText: _.first(question.options)?.optionText,
            endText: _.last(question.options)?.optionText,
          }}
          showMarks={true}
          colorScheme={
            question.firsOptionIsPositive ? SliderColorSchemeEnum.GreenToRed : SliderColorSchemeEnum.RedToGreen
          }
          onChange={() => undefined}
        />
      );
    case QuestionType.FreeInput:
      return <AppTextField type={'text-area'} labelProps={labelProps} skipProps={skipProps} value={''} />;
    default:
      return <MobileUnsupported questionType={question.questionType} />;
  }
};

const renderMobilePreview = (
  question: ISurveyQuestion,
  answerValue: string | undefined,
  checkedValues: string[] | undefined,
  onDefaultValueChange?: (optionValue: string | null) => void,
) => {
  const pinControlsToBottom = shouldPinControlsToBottom(question.questionType);
  if (isContentQuestion(question.questionType)) {
    return <ContentPreview question={question} />;
  }

  switch (question.questionType) {
    case QuestionType.SingleChose:
      return (
        <div className={clsx(Style.mobileOptions, { [Style.mobileControlsBottom]: pinControlsToBottom })}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <MobileAttentionText text={question.attentionText} centered={true} />
          <div className={Style.mobileControlsGroup}>
            {question.options.map((option, index) => (
              <div
                key={option.optionValue}
                className={Style.mobileSingleOption}
                data-selected={answerValue === option.optionValue}
                onClick={() => onDefaultValueChange?.(option.optionValue)}
              >
                <span className={Style.mobileOptionIndex}>{index}</span>
                <span>{option.optionText}</span>
                {answerValue === option.optionValue && <span className={Style.mobileCheck}>{'✓'}</span>}
              </div>
            ))}
          </div>
        </div>
      );
    case QuestionType.MultipleChose:
      return (
        <div className={clsx(Style.mobileOptions, { [Style.mobileControlsBottom]: pinControlsToBottom })}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <MobileAttentionText text={question.attentionText} centered={true} />
          <div className={Style.mobileControlsGroup}>
            {question.options.map((option) => (
              <div
                key={option.optionValue}
                className={Style.mobileCheckboxOption}
                data-selected={checkedValues?.includes(option.optionValue)}
                onClick={() => onDefaultValueChange?.(option.optionValue)}
              >
                <div className={Style.mobileCheckbox}>{checkedValues?.includes(option.optionValue) ? '✓' : ''}</div>
                <span>{option.optionText}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case QuestionType.YesNo:
      return (
        <div className={clsx(Style.mobileYesNo, { [Style.mobileControlsBottom]: pinControlsToBottom })}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <MobileAttentionText text={question.attentionText} centered={true} />
          <div className={Style.mobileYesNoButtons}>
            {question.options.map((option) => (
              <div
                key={option.optionValue}
                className={Style.mobileYesNoOption}
                onClick={() => onDefaultValueChange?.(option.optionValue)}
              >
                <div className={Style.mobileYesNoCircle} data-selected={answerValue === option.optionValue}>
                  {option.optionValue === 'true' ? '✓' : '✕'}
                </div>
                <span>{option.optionText}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case QuestionType.DateInput:
      return (
        <div className={Style.mobileForm}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <div className={Style.mobileLabel}>{question.options[0]?.optionText || 'Date'}</div>
          <div className={Style.mobileInput}>{'TT/MM/JJJJ'}</div>
          <MobileAttentionText text={question.attentionText} />
        </div>
      );
    case QuestionType.DateRangeInput:
      return (
        <div className={Style.mobileForm}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          {question.options.map((option) => (
            <React.Fragment key={option.optionValue}>
              <div className={Style.mobileLabel}>{option.optionText}</div>
              <div className={Style.mobileInput}>{'TT/MM/JJJJ'}</div>
            </React.Fragment>
          ))}
          <MobileAttentionText text={question.attentionText} />
        </div>
      );
    case QuestionType.FreeInput:
      return (
        <div className={Style.mobileForm}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <div className={Style.mobileLabel}>{question.options[0]?.optionText || 'Comment'}</div>
          <div className={Style.mobileTextArea}>{'Describe your answer...'}</div>
          <MobileAttentionText text={question.attentionText} />
        </div>
      );
    case QuestionType.Scale:
      return (
        <div className={clsx(Style.mobileScale, { [Style.mobileControlsBottom]: pinControlsToBottom })}>
          <div className={Style.mobileSurveyTitle}>{mobileSurveyTitle}</div>
          <div className={Style.mobileQuestionTitle}>{question.questionText}</div>
          <MobileAttentionText text={question.attentionText} centered={true} />
          <div className={Style.mobileControlsGroup}>
            <div className={Style.mobileScaleValue}>{String(question.options?.[0]?.scoreValue ?? 0)}</div>
            <div className={Style.mobileScaleTrack}>
              {question.options.map((option) => (
                <span key={option.optionValue} className={Style.mobileScaleMark} />
              ))}
            </div>
          </div>
        </div>
      );
    default:
      return <MobileUnsupported questionType={question.questionType} />;
  }
};

type QuestionPreviewProps = {
  question: ISurveyQuestion | null;
  index?: number;
  total?: number;
  onPrev?: () => void;
  onNext?: () => void;
  onSelectQuestion?: (questionId: string) => void;
  onDefaultValueChange?: (optionValue: string | null) => void;
};

export const QuestionPreview = ({
  question,
  index,
  total,
  onPrev,
  onNext,
  onDefaultValueChange,
}: QuestionPreviewProps) => {
  const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop');
  const { answerValue, scoreValue, checkedValues } = useSelectedValues(question);

  if (!question) {
    return (
      <div className={Style.emptyState}>
        <div className={Style.toolbar}>
          <div className={Style.modeSwitch}>
            <button className={Style.modeButton} data-active={true}>
              {'Desktop'}
            </button>
            <button className={Style.modeButton}>{'Mobile'}</button>
          </div>
        </div>
        <div className={Style.emptyInner}>
          <div className={Style.emptyTitle}>{'Live Preview'}</div>
          <div className={Style.emptyText}>{'Select a question to preview how it will appear to respondents.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={Style.stage}>
      <div className={Style.toolbar}>
        <div className={Style.previewMeta}>
          <div className={Style.previewIndex}>{index ?? 1}</div>
          <div>
            <div className={Style.previewTitle}>{question.questionText || 'Untitled question'}</div>
            <div className={Style.previewSubtitle}>{QuestionType[question.questionType]}</div>
          </div>
        </div>
        <div className={Style.toolbarActions}>
          <div className={Style.modeSwitch}>
            <button
              className={Style.modeButton}
              data-active={previewMode === 'desktop'}
              onClick={() => setPreviewMode('desktop')}
            >
              {'Desktop'}
            </button>
            <button
              className={Style.modeButton}
              data-active={previewMode === 'mobile'}
              onClick={() => setPreviewMode('mobile')}
            >
              {'Mobile'}
            </button>
          </div>
          <AppButton
            variant={'icon-link'}
            colorSchema={'primary'}
            text="Reset"
            onClick={() => setPreviewMode('desktop')}
          />
        </div>
      </div>

      <div className={Style.canvas}>
        {previewMode === 'desktop' ? (
          <div className={Style.desktopPreview}>
            <div className={Style.desktopCard}>
              {renderDesktopPreview(question, String(index ?? 1), answerValue, scoreValue, checkedValues, onDefaultValueChange)}
            </div>
          </div>
        ) : (
          <MobileChrome>
            {renderMobilePreview(question, answerValue, checkedValues, onDefaultValueChange)}
            <MobileNav onPrev={onPrev} onNext={onNext} counterText={`${index ?? 1} of ${total ?? 1}`} />
          </MobileChrome>
        )}
      </div>

      <div className={Style.navigation}>
        <button className={Style.navButtonLarge} onClick={onPrev}>
          {'←'}
        </button>
        <div className={Style.navCounter}>{`${index ?? 1} / ${total ?? 1}`}</div>
        <button className={Style.navButtonLarge} onClick={onNext}>
          {'→'}
        </button>
      </div>
    </div>
  );
};
